import React, { useState, useEffect, useCallback } from 'react';
import { Inbox, Loader2, Phone, Mail, Building2, User, RefreshCw } from 'lucide-react';
import { leadsApi } from '../../services/api';
import { toast } from 'sonner';

const STATUS_STYLES = {
  new: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  contacted: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  closed: 'bg-slate-500/15 text-slate-400 border-slate-500/40',
};

const SOURCE_META = {
  'dsc-renewal': { label: 'DSC Renewal', cls: 'bg-amber-500/15 text-amber-400 border border-amber-500/30' },
  'din-renewal': { label: 'DIN Renewal', cls: 'bg-orange-500/15 text-orange-400 border border-orange-500/30' },
  'mp-startup-benefits': { label: 'MP Startup', cls: 'bg-purple-500/15 text-purple-400 border border-purple-500/30' },
  'website': { label: 'Website', cls: 'bg-slate-700/60 text-slate-300 border border-slate-600' },
};

const STATUS_FILTERS = [
  { key: '', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'closed', label: 'Closed' },
];

const SOURCE_FILTERS = [
  { key: '', label: 'All Sources' },
  { key: 'dsc-renewal', label: 'DSC Renewal' },
  { key: 'din-renewal', label: 'DIN Renewal' },
  { key: 'mp-startup-benefits', label: 'MP Startup' },
  { key: 'website', label: 'Website' },
];

export default function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const data = await leadsApi.list(statusFilter || undefined);
      setLeads(data.leads || []);
    } catch (e) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const updateStatus = async (id, status) => {
    try {
      await leadsApi.updateStatus(id, status);
      setLeads((prev) => prev.map((ld) => (ld.id === id ? { ...ld, status } : ld)));
      toast.success('Lead status updated');
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const filtered = sourceFilter ? leads.filter((ld) => ld.source === sourceFilter) : leads;

  // Counts per source (for filter badges)
  const counts = leads.reduce((acc, ld) => {
    acc[ld.source] = (acc[ld.source] || 0) + 1;
    return acc;
  }, {});
  const renewalCount = (counts['dsc-renewal'] || 0) + (counts['din-renewal'] || 0);

  return (
    <div className="space-y-4" data-testid="leads-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Inbox className="w-5 h-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-white">Leads & Requests</h2>
          <span className="text-xs text-slate-400">({filtered.length})</span>
          {renewalCount > 0 && (
            <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid="renewal-count-badge">
              <RefreshCw className="w-3 h-3 inline mr-1" />{renewalCount} renewal{renewalCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button onClick={loadLeads} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="leads-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wider text-slate-500 mr-1">Status:</span>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`leads-filter-${f.key || 'all'}`}
            onClick={() => setStatusFilter(f.key)}
            className={`h-7 px-3 text-xs rounded-full border transition-colors ${
              statusFilter === f.key
                ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Source filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wider text-slate-500 mr-1">Source:</span>
        {SOURCE_FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`leads-source-${f.key || 'all'}`}
            onClick={() => setSourceFilter(f.key)}
            className={`h-7 px-3 text-xs rounded-full border transition-colors ${
              sourceFilter === f.key
                ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
            }`}
          >
            {f.label}
            {f.key && counts[f.key] ? <span className="ml-1 text-[10px] opacity-70">({counts[f.key]})</span> : null}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading leads...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 rounded-xl bg-slate-800/50 border border-slate-700">
          <Inbox className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No leads match the current filter.</p>
          <p className="text-slate-500 text-xs mt-1">Renewal requests from the client portal appear here alongside MP Startup submissions.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((lead) => {
            const src = SOURCE_META[lead.source] || { label: lead.source, cls: 'bg-slate-700/60 text-slate-300 border border-slate-600' };
            const isRenewal = lead.source === 'dsc-renewal' || lead.source === 'din-renewal';
            return (
              <div key={lead.id}
                className={`p-4 rounded-xl border ${isRenewal ? 'bg-amber-500/5 border-amber-500/25' : 'bg-slate-800/50 border-slate-700'}`}
                data-testid="lead-row">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-medium" data-testid="lead-name">{lead.name}</p>
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`}>
                        {lead.status}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded ${src.cls}`} data-testid={`lead-source-${lead.source}`}>
                        {src.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 flex-wrap text-xs text-slate-400">
                      <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-emerald-400">
                        <Phone className="w-3 h-3" /> {lead.phone}
                      </a>
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-emerald-400">
                          <Mail className="w-3 h-3" /> {lead.email}
                        </a>
                      )}
                      <span>{lead.created_at ? new Date(lead.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}</span>
                    </div>
                    <p className={`text-sm mt-2 ${isRenewal ? 'text-amber-300' : 'text-emerald-400/90'}`}>{lead.position}</p>
                    {lead.context && (lead.context.company_name || lead.context.director_name) && (
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-300 flex-wrap">
                        {lead.context.company_name && (
                          <span className="flex items-center gap-1"><Building2 className="w-3 h-3 text-slate-500" />{lead.context.company_name}</span>
                        )}
                        {lead.context.director_name && (
                          <span className="flex items-center gap-1"><User className="w-3 h-3 text-slate-500" />Director: {lead.context.director_name}</span>
                        )}
                      </div>
                    )}
                    {lead.message && <p className="text-sm text-slate-400 mt-2 whitespace-pre-line">{lead.message}</p>}
                  </div>
                  <select
                    value={lead.status}
                    onChange={(e) => updateStatus(lead.id, e.target.value)}
                    data-testid="lead-status-select"
                    className="h-8 px-2 text-xs bg-slate-800 border border-slate-700 rounded-md text-white"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
